import {Select, SelectProps} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  containerAlertTypes,
  DatabaseCode,
  siteAlertTypes,
  transportCategories,
  valveTypes
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";

export type DbCodeSelectProps<D extends DatabaseCode> = Omit<
  BaseDbCodeSelectProps<D>,
  "options"
> & {
  options?: D[];
};

export type BaseDbCodeSelectProps<D extends DatabaseCode> = Omit<SelectProps, "defaultValue" | "data"> & {
  defaultValue?: D;
  options: D[];
  onDbCodeChange?: (dbCode: D[]) => void;
  sort?: boolean
};

export function BaseDbCodeSelect<D extends DatabaseCode>(
  props: BaseDbCodeSelectProps<D>
) {
  const {
    defaultValue,
    options,
    onChange,
    onDbCodeChange,
    sort = true,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);

  const _onChange = useCallback(
    (value: string) => {
      if (onDbCodeChange) {
        let items: D[] = [];

        if (value) {
          items = options.filter((d) => d.code === value);
        }

        onDbCodeChange(items);
      }

      if (onChange) {
        onChange(value);
      }
    },
    [options, onChange, onDbCodeChange]
  );

  const data = useMemo(() => {
    const data = options.map((type) => {
      return {
        value: type.code,
        label: t<string>(type.displayValue.id, type.displayValue.defaultMessage)
      };
    })

    if (!sort) {
      return data
    }

    return data.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
  }, [options, t, sort]);

  return (
    <Select data={data} onChange={_onChange} defaultValue={defaultValue?.code} {...rest} />
  );
}

type DbCodeAnySelectProps = Omit<BaseDbCodeSelectProps<DatabaseCode>, 'options'> & {
  codeType: string
};

export const DbCodeSelect = (props: DbCodeAnySelectProps) => {
  const {codeType, ...rest} = props;

  const options: DatabaseCode[] = useMemo(() => {
    if (codeType === 'TRANSPORT_CATEGORY') {
      return transportCategories
    } else if (codeType === 'VALVE_TYPE') {
      return valveTypes
    } else if (codeType === 'FIELD_ALERT_DETECTION') {
      return [
        {code: 'MANUAL', displayValue: {id: 'common.entity.fieldalert.detection.manual', defaultMessage: 'Manual'}},
        {code: 'AUTO', displayValue: {id: 'common.entity.fieldalert.detection.auto', defaultMessage: 'Auto'}}
      ]
    } else if (codeType === "FIELD_ALERT_TYPE") {
      return [
        ...Object.values(containerAlertTypes),
        ...Object.values(siteAlertTypes)
      ]
    }

    return []
  }, [codeType])

  return (
    <BaseDbCodeSelect
      {...rest}
      options={options}/>
  )
};
